/**
 * Framework-agnostic media client — ported from conferweb setMediaCall.js.
 *
 * Adaptations vs. the source:
 *  1. ICE servers come from injected config.iceServers (not Redux store).
 *  2. muteMic / micMuteState removed (no React/Redux dependency).
 *  3. iceLogger replaced with injected logger (defaults to console subset).
 *  4. Results surface via injected handlers (onRemoteStream/onEnded/onFailure)
 *     instead of Redux dispatch.
 *
 * The X-XOP header format and ua.call(String(sessionId), ...) destination are
 * preserved verbatim from the source.
 *
 * SDP direction patching is applied in screen-share-viewer mode only
 * (viewScreen), mirroring conferweb's isScreenShareViewer guard. It is
 * exposed as patchAnswerSdp() for direct unit testing.
 */

import JsSIP from 'jssip';
import type {
  MediaClient,
  MediaClientConfig,
  MediaCallHandlers,
  MediaSession,
  JoinParams,
  ConsultationParams,
  ScreenShareParams,
  ViewScreenParams,
  CameraShareParams,
  ViewCameraParams,
} from './types';

// TODO(tune): port full ICE recovery (iceLogger stats, srflx/relay fast-exit,
// candidateCheckTimeout, getStats analysis). These are only validatable against
// a live media server and are explicitly deferred per task instructions.

// Concise call-media diagnostics. Visible in the browser console AND the macOS
// desktop WKWebView inspector (Safari ▸ Develop). Lets us see exactly which leg
// (base / camera / screen, publish / view) reaches which lifecycle stage when a
// remote video isn't rendering — without it the desktop is a black box.
function dbg(...args: unknown[]): void {
  try {
    // eslint-disable-next-line no-console
    console.info('[xop-call]', ...args);
  } catch {
    /* console unavailable */
  }
}

// ---------------------------------------------------------------------------
// SDP viewer patch (Phase 3 — screen-share receive-only mode)
// Faithful port of the isScreenShareViewer branch in conferweb setMediaCall.js.
// The Matrix SFU sometimes returns wrong/absent direction lines on screen answers;
// this rewrites a=recvonly→a=sendonly and inserts a=sendonly when absent.
// Exported for direct unit testing.
// ---------------------------------------------------------------------------
export function patchAnswerSdp(sdp: string): string {
  const lines = sdp.split('\r\n');
  const out: string[] = [];
  let section: 'audio' | 'video' | null = null;
  let hasDir = false;

  for (const line of lines) {
    if (line.startsWith('m=audio') || line.startsWith('m=video')) {
      // Close previous section: inject sendonly if no direction was present
      if (section !== null && !hasDir) {
        out.push('a=sendonly');
      }
      section = line.startsWith('m=audio') ? 'audio' : 'video';
      hasDir = false;
    }

    if (section !== null && /^a=(sendrecv|recvonly|sendonly|inactive)$/.test(line)) {
      hasDir = true;
      if (line === 'a=recvonly') {
        out.push('a=sendonly');
        continue; // skip the original recvonly line
      }
    }

    out.push(line);
  }

  // Close the final section if no direction was written
  if (section !== null && !hasDir) {
    // Insert before the trailing empty string (if present) so the SDP ends cleanly
    const idx = out[out.length - 1] === '' ? out.length - 1 : out.length;
    out.splice(idx, 0, 'a=sendonly');
  }

  return out.join('\r\n');
}

// ---------------------------------------------------------------------------
// Shared UA builder — avoids duplicating the UA construction between join(),
// shareScreen(), and viewScreen().
// ---------------------------------------------------------------------------
function buildUA(
  config: MediaClientConfig,
  sessionId: number,
  displayName: string,
): InstanceType<typeof JsSIP.UA> {
  return new JsSIP.UA({
    sockets: [new JsSIP.WebSocketInterface(config.mediaWsUrl)],
    uri: `sip:${sessionId}@${config.sipDomain}`,
    display_name: displayName,
    register: false,
    connection_recovery_min_interval: 9999,
    connection_recovery_max_interval: 9999,
  });
}

// Wire remote-stream events on a peer connection.
function wireRemoteStream(conn: RTCPeerConnection, onRemoteStream: (s: MediaStream) => void): void {
  conn.addEventListener('addstream', (e: Event) => {
    const streamEvent = e as Event & { stream?: MediaStream };
    if (streamEvent.stream) {
      onRemoteStream(streamEvent.stream);
    }
  });
  conn.addEventListener('track', (e: RTCTrackEvent) => {
    // WebKit (the macOS desktop WKWebView) frequently fires 'track' for a
    // RECEIVE-ONLY leg (viewCamera / viewScreen) with an EMPTY `e.streams`
    // array — there is no local track and the SFU's answer may omit the stream
    // (msid) association, so WebKit doesn't synthesise one (Chromium does).
    // Without this fallback the desktop viewer never receives a stream, so the
    // peer's camera/screen never renders — even though the base AUDIO call works
    // (it is sendrecv, which DOES carry a stream). Build a stream from the track
    // itself when none is attached. Harmless on Chromium (e.streams[0] wins).
    const stream = e.streams[0] ?? (e.track ? new MediaStream([e.track]) : null);
    dbg('remote track:', e.track?.kind, '· e.streams:', e.streams.length, '· delivered:', !!stream);
    // Diagnostic: catch the SFU silently STOPPING forwarding (the JsSIP leg stays
    // up but the inbound track goes mute/ended) — the prime suspect for a 3rd
    // joiner blanking the existing video without any re-INVITE/teardown.
    const t = e.track;
    // Diagnostic-only — must never throw (a test/webview track may lack
    // addEventListener), so guard before wiring the mute/unmute/ended logs.
    if (t && typeof t.addEventListener === 'function') {
      t.addEventListener('mute', () => console.info(`[call] remote ${t.kind} track MUTED (SFU stopped forwarding?)`));
      t.addEventListener('unmute', () => console.info(`[call] remote ${t.kind} track UNMUTED (forwarding resumed)`));
      t.addEventListener('ended', () => console.info(`[call] remote ${t.kind} track ENDED`));
    }
    if (stream) {
      onRemoteStream(stream);
    }
  });
}

// ---------------------------------------------------------------------------
// Consultation X-XOP header — the OUTBOUND DIAL routing context.
// Format: <conferenceId>/<moderatorPin>   (2-part, ported from H&H consultation
// `X-XOP: ${station.id}/${pins.moderator}`). The INVITE's user part is the PSTN
// number (not a sessionId), so C1 originates the PSTN leg under the moderator
// context and bridges it. ⚠ The exact first field (conferenceId vs roomId) is our
// best mapping of the reference's `station.id` onto our ephemeral room — VERIFY
// against a live C1 dial and adjust HERE if the leg doesn't originate.
// ---------------------------------------------------------------------------
function consultationXopHeader(conferenceId: number, moderatorPin: string): string {
  return `X-XOP: ${[conferenceId, moderatorPin].join('/')}`;
}

// ---------------------------------------------------------------------------
// Screen X-XOP header builder (s/s variant)
// Format: <conferenceId>/<participantPin>/<meetingId>/<ownSessionId>/s/s
// ---------------------------------------------------------------------------
function screenXopHeader(
  conferenceId: number,
  participantPin: string,
  meetingId: number,
  ownSessionId: number,
): string {
  return `X-XOP: ${[conferenceId, participantPin, meetingId, ownSessionId, 's', 's'].join('/')}`;
}

// ---------------------------------------------------------------------------
// Camera X-XOP headers (SFU camera variant — see conferweb getCameraHeaderConfig)
// Publisher: <conf>/<pin>/<meeting>/<ownSid>/v-<ownSid>/s
// Viewer:    <conf>/<pin>/<meeting>/<ownSid>/v-<sharerSid>/r
// (Screen uses /s/s; camera replaces the suffix with v-<targetSid>/{s|r}.)
// ---------------------------------------------------------------------------
function cameraPublishXopHeader(
  conferenceId: number,
  participantPin: string,
  meetingId: number,
  ownSessionId: number,
): string {
  return `X-XOP: ${[conferenceId, participantPin, meetingId, ownSessionId, `v-${ownSessionId}`, 's'].join('/')}`;
}

function cameraViewXopHeader(
  conferenceId: number,
  participantPin: string,
  meetingId: number,
  ownSessionId: number,
  sharerSessionId: number,
): string {
  return `X-XOP: ${[conferenceId, participantPin, meetingId, ownSessionId, `v-${sharerSessionId}`, 'r'].join('/')}`;
}

// ---------------------------------------------------------------------------
// pcConfig shared between all call types
// ---------------------------------------------------------------------------
function makePcConfig(iceServers: RTCIceServer[], relayOnly = false): RTCConfiguration {
  return {
    iceServers,
    bundlePolicy: 'max-compat',
    // C1 is TURN-RELAY-ONLY: "UDP STUN on :443 is a black hole" (see docs/calls.md
    // → fast handshake). The base audio call still connects (it trickles to a
    // relay), but the SEPARATE camera/screen SFU legs were NOT reliably bringing
    // up media on the macOS desktop (WKWebView) — a WebRTC loopback in WebKit
    // proved negotiation + the remote track are fine, so the failure is the ICE
    // MEDIA path: a doomed srflx/host (UDP) candidate can win the offer and stall
    // the leg. Forcing `relay` makes ICE gather ONLY TURN-relay candidates (the
    // one transport that works against C1) — this is the "web→desktop camera/
    // screen never starts" fix. The base call is left on 'all' (it works today).
    iceTransportPolicy: relayOnly ? 'relay' : 'all',
    iceCandidatePoolSize: 0,
    rtcpMuxPolicy: 'require',
  };
}

// ---------------------------------------------------------------------------
// placeCall — the one INVITE primitive shared by join / shareScreen /
// viewScreen / shareCamera / viewCamera. Centralising it means the critical
// fixes apply uniformly:
//   • Receive-only legs pass mediaConstraints {audio:false,video:false} so
//     JsSIP NEVER calls getUserMedia (the bug: a viewer was prompted for the
//     camera) and rtcOfferConstraints {offerToReceiveVideo:true} so the SFU
//     actually relays the publisher's video down to us.
//   • hangup() AND every failed/ended both terminate the RTCSession *and*
//     ua.stop() — closing the JsSIP WebSocket so the SFU frees the port
//     (the "6 leaked video ports" symptom).
//   • A `settled` guard prevents a late confirmed/ended from double-resolving.
// ---------------------------------------------------------------------------
// JsSIP holds the INVITE until ICE gathering COMPLETES (~5-10s; and against C1,
// UDP STUN on :443 is a black hole so "complete" waits out doomed timeouts every
// call). We release the offer as soon as a usable candidate appears (see the ICE
// fast-exit in placeCall), bounded by this ceiling. Ported from the reference
// setMediaCall.js (RW-1586). "Wait just enough" — connect the moment a path exists.
const ICE_FAST_EXIT_MS = 3000;

interface PlaceCallOptions {
  /** Build a FRESH JsSIP UA (its own WebSocket) for an attempt. Called once per
   *  attempt so a retry gets a clean socket — the failed attempt's UA is stopped. */
  buildUa: () => InstanceType<typeof JsSIP.UA>;
  /** The SIP user part to dial. A numeric sessionId for room/SFU legs, or a PSTN
   *  phone number string for the consultation outbound dial. */
  dialTarget: number | string;
  extraHeaders: string[];
  pcConfig: RTCConfiguration;
  /** Present → publisher (we send this stream). Absent → receive-only. */
  mediaStream?: MediaStream;
  /** Receive-only legs set {audio:false,video:false} to suppress getUserMedia. */
  mediaConstraints?: { audio: boolean; video: boolean };
  rtcOfferConstraints?: { offerToReceiveAudio: boolean; offerToReceiveVideo: boolean };
  /** Wrap setRemoteDescription with patchAnswerSdp (viewer legs). */
  applyViewerPatch?: boolean;
  /** Add a recvonly VIDEO transceiver before the offer (receive-only video legs).
   *  REQUIRED for WebKit/macOS WKWebView: it ignores the legacy
   *  `offerToReceiveVideo` RTCOfferOptions, so without an explicit transceiver the
   *  viewer's offer has NO video m-line and the SFU relays nothing (the macOS
   *  desktop "video never starts" bug). Chromium honors the legacy constraint and
   *  reuses this same transceiver, so there's no duplicate m-line. */
  addRecvVideoTransceiver?: boolean;
  /** Report ICE connection-state changes to handlers.onConnectionState. */
  reportConnectionState?: boolean;
  failLabel: string;
  disconnectLabel: string;
}

// ---------------------------------------------------------------------------
// Transient-failure retry (the group / 1:1 "camera goes black" fix)
// ---------------------------------------------------------------------------
// The C1 SFU intermittently answers a leg's INVITE with SDP the native
// RTCPeerConnection REJECTS ("Incompatible SDP") when several leg INVITEs race —
// a 3rd person joining fires the base call + each camera VIEW + the camera PUBLISH
// in one burst (each opens its own JsSIP UA/WebSocket to the SFU), and they
// collide. The reference webcast client multiplexes every leg over ONE shared UA
// and never hits this; our per-leg UAs do. A staggered, jittered retry de-conflicts
// them — the SAME recovery proven live (a 1:1 view leg that failed "Incompatible
// SDP" then succeeded on a fresh re-subscribe). The happy path is byte-identical:
// a leg that confirms on the first attempt never schedules a retry. Permanent
// causes (Canceled / Busy / Rejected / Unavailable / Not Found / Authentication /
// the peer hanging up) are terminal — retrying them would only spin.
const RETRYABLE_CAUSES = new Set<string>([
  'Incompatible SDP',
  'Bad Media Description',
  'Connection Error',
  'WebSocket Error',
  'Request Timeout',
  'RTP Timeout',
  'Internal Error',
  'SIP Failure Code',
]);
const MAX_LEG_ATTEMPTS = 3;
const RETRY_DELAY_BASE_MS = 600;

function placeCall(opts: PlaceCallOptions, handlers: MediaCallHandlers): Promise<MediaSession> {
  // NB: only `resolve` — a terminal failure resolves a no-op session (callers
  // detect it via onFailure/onEnded), so the promise never rejects.
  return new Promise<MediaSession>((resolve) => {
    const { dialTarget, extraHeaders, pcConfig } = opts;
    // `settled` spans ALL attempts: once a leg confirms (or fails terminally) the
    // promise is resolved and stale events from any prior attempt are ignored.
    let settled = false;
    let attempt = 0;

    const finishFailure = (cause: string): void => {
      dbg(`${opts.failLabel}: FAILED — ${cause}`);
      handlers.onFailure(new Error(cause));
      if (!settled) {
        settled = true;
        resolve({ hangup() { /* already torn down */ } });
      }
    };

    const attemptCall = (): void => {
      attempt += 1;
      const isLastAttempt = attempt >= MAX_LEG_ATTEMPTS;
      let ua: InstanceType<typeof JsSIP.UA>;
      try {
        ua = opts.buildUa();
      } catch (e) {
        finishFailure((e as Error)?.message ?? opts.disconnectLabel);
        return;
      }

      let jsSipSession: ReturnType<typeof ua.call> | null = null;
      let pc: RTCPeerConnection | null = null;
      // Per-attempt guard: once THIS attempt confirms/fails/ends, ignore its later
      // events (JsSIP can fire `ended` right after `failed`, which would otherwise
      // resolve a dead session while a retry is already scheduled).
      let attemptSettled = false;
      // True once THIS attempt's leg actually established. Distinguishes a real
      // end-of-call BYE (→ handlers.onEnded, the window-close + call-record path)
      // from a BYE/end that arrives BEFORE the leg ever confirmed (transient —
      // retry it like a failure).
      let wasConfirmed = false;

      // ── ICE fast-exit state (see registration in the connected handler) ──────
      const iceUrls = (pcConfig.iceServers ?? []).flatMap((s) =>
        Array.isArray(s.urls) ? s.urls : [s.urls],
      );
      const hasTurnServer = iceUrls.some((u) => typeof u === 'string' && /^turns?:/i.test(u));
      const sendsVideo = (opts.mediaStream?.getVideoTracks?.() ?? []).length > 0;
      const wantsRecvVideo = opts.rtcOfferConstraints?.offerToReceiveVideo === true;
      const isAudioOnly = !sendsVideo && !wantsRecvVideo;
      let iceTimer: ReturnType<typeof setTimeout> | null = null;
      let iceReleased = false;
      let foundSrflx = false;
      let foundRelay = false;
      let lastReady: (() => void) | null = null;
      const clearIceTimer = (): void => {
        if (iceTimer !== null) {
          clearTimeout(iceTimer);
          iceTimer = null;
        }
      };
      const releaseIce = (ready: (() => void) | null): void => {
        if (iceReleased) return;
        iceReleased = true;
        clearIceTimer();
        dbg(`${opts.failLabel}: ICE released → sending offer`);
        try {
          ready?.();
        } catch {
          /* session may already be torn down */
        }
      };

      const stopUa = (): void => {
        try {
          ua.stop();
        } catch {
          /* already stopped */
        }
      };

      // Decide retry vs. terminal failure for THIS attempt's failure/disconnect.
      const onLegLost = (cause: string): void => {
        if (attemptSettled) return;
        attemptSettled = true;
        clearIceTimer();
        stopUa(); // free this attempt's socket before a fresh one
        if (!settled && !isLastAttempt && RETRYABLE_CAUSES.has(cause)) {
          // Jitter so several legs retrying at once don't re-collide in lockstep.
          const delay = RETRY_DELAY_BASE_MS * attempt + Math.floor(Math.random() * 500);
          dbg(`${opts.failLabel}: ${cause} → retry ${attempt + 1}/${MAX_LEG_ATTEMPTS} in ${delay}ms`);
          setTimeout(() => { if (!settled) attemptCall(); }, delay);
          return;
        }
        finishFailure(cause);
      };

      const liveSession = (sess: ReturnType<typeof ua.call>): MediaSession => ({
        hangup() {
          clearIceTimer();
          try {
            (sess as any)?.terminate?.();
          } catch {
            /* already terminated */
          }
          stopUa();
        },
        async replaceTrack(track: MediaStreamTrack) {
          const sender = pc?.getSenders().find((s) => s.track?.kind === track.kind);
          if (sender) await sender.replaceTrack(track);
        },
      });

      const eventHandlers = {
        confirmed(_e: unknown) {
          if (attemptSettled || settled) return;
          if (jsSipSession) {
            attemptSettled = true;
            wasConfirmed = true;
            settled = true;
            dbg(`${opts.failLabel}: confirmed (call established)`);
            resolve(liveSession(jsSipSession));
          }
        },
        failed(e: unknown) {
          const cause = (e as { cause?: string } | undefined)?.cause ?? opts.failLabel;
          onLegLost(cause);
        },
        ended(_e: unknown) {
          // BYE/termination AFTER the leg established → a REAL end-of-call: notify
          // the caller (closes the window, posts the call record). This must fire
          // even though `attemptSettled` is already true from `confirmed`.
          if (wasConfirmed) {
            clearIceTimer();
            dbg(`${opts.failLabel}: ended`);
            stopUa();
            handlers.onEnded();
            return;
          }
          // A late `ended` from an attempt that already failed/retried → ignore.
          if (attemptSettled) return;
          // `ended` BEFORE the leg ever confirmed (e.g. the SFU 200-OKs then
          // immediately BYEs a conflicted session during the join burst) is as
          // transient as a 4xx — retry it instead of tearing the UI down.
          dbg(`${opts.failLabel}: ended before confirm → transient`);
          onLegLost('Connection Error');
        },
      };

      // Receive-only video legs need an explicit recvonly transceiver added BEFORE
      // JsSIP builds the offer — WebKit ignores `offerToReceiveVideo`. JsSIP emits
      // 'peerconnection' synchronously when it creates the PC, before createOffer.
      const extraHandlers: Record<string, (arg: { peerconnection?: RTCPeerConnection }) => void> = {};
      if (opts.addRecvVideoTransceiver) {
        extraHandlers.peerconnection = (data) => {
          try {
            data.peerconnection?.addTransceiver('video', { direction: 'recvonly' });
            dbg(`${opts.failLabel}: added recvonly video transceiver (WebKit ignores offerToReceiveVideo)`);
          } catch (e) {
            dbg(`${opts.failLabel}: addTransceiver(video) failed`, (e as Error)?.message);
          }
        };
      }

      ua.on('connected', () => {
        const callOptions = {
          sessionTimersExpires: 120,
          eventHandlers: { ...eventHandlers, ...extraHandlers },
          pcConfig,
          extraHeaders,
          ...(opts.mediaStream ? { mediaStream: opts.mediaStream } : {}),
          ...(opts.mediaConstraints ? { mediaConstraints: opts.mediaConstraints } : {}),
          ...(opts.rtcOfferConstraints ? { rtcOfferConstraints: opts.rtcOfferConstraints } : {}),
        };

        jsSipSession = ua.call(String(dialTarget), callOptions);
        dbg(`${opts.failLabel}: placing INVITE → ${dialTarget}`,
          `(recvVideo=${wantsRecvVideo}, sendsVideo=${sendsVideo}, turn=${hasTurnServer}, attempt=${attempt})`);

        if (!jsSipSession) {
          onLegLost('Internal Error');
          return;
        }

        // ── ICE fast-exit ────────────────────────────────────────────────────
        // JsSIP fires 'icecandidate' per candidate with a ready() that, when
        // called, stops waiting for gathering-complete and sends the INVITE now.
        // Release on the FIRST usable candidate: srflx for an audio-only/no-TURN
        // leg (don't wait on a relay that may never come), relay otherwise; a 3s
        // ceiling guards the case where neither arrives. This collapses the
        // ~5-10s "wait for gathering to complete" into "connect when a path
        // exists" — the bulk of the perceived handshake delay.
        iceTimer = setTimeout(() => {
          // Ceiling reached: ship the INVITE with whatever we gathered (srflx /
          // host) even if the preferred relay never arrived. The OLD guard
          // `if (foundSrflx || foundRelay) return` meant a video leg that found a
          // srflx but was still waiting for a relay (which never came — e.g. a
          // WKWebView leg whose TURN allocation lagged) would HANG forever: no
          // offer sent, no media, no remote track. This is the "web→desktop video
          // never starts" hang. releaseIce is idempotent, so a relay that already
          // released is unaffected.
          dbg(`${opts.failLabel}: ICE ceiling (${ICE_FAST_EXIT_MS}ms) → releasing with gathered candidates`);
          releaseIce(lastReady);
        }, ICE_FAST_EXIT_MS);

        (jsSipSession as unknown as {
          on?: (e: string, cb: (a: { candidate?: RTCIceCandidate; ready?: () => void }) => void) => void;
        }).on?.('icecandidate', ({ candidate, ready }) => {
          if (ready) lastReady = ready;
          const candStr = candidate?.candidate ?? '';
          if (candStr.indexOf('typ srflx') > -1) {
            foundSrflx = true;
            if (isAudioOnly || !hasTurnServer) releaseIce(ready ?? lastReady);
            return;
          }
          if (candStr.indexOf('typ relay') > -1) {
            foundRelay = true;
            releaseIce(ready ?? lastReady);
          }
        });

        // Diagnostic: a re-INVITE / renegotiation fires when conference membership
        // changes (a 3rd person joins/leaves). If the existing video breaks then,
        // this is where to look — the SFU re-offers and the leg must re-apply it.
        (jsSipSession as unknown as {
          on?: (e: string, cb: (a: unknown) => void) => void;
        }).on?.('reinvite', () => console.info(`[call] ${opts.failLabel}: RE-INVITE received (SFU renegotiation)`));

        const conn = (jsSipSession as unknown as { connection?: RTCPeerConnection }).connection;
        pc = conn ?? null;
        if (conn) {
          wireRemoteStream(conn, handlers.onRemoteStream);
          conn.addEventListener('signalingstatechange', () => console.info(`[call] ${opts.failLabel}: signalingState=${conn.signalingState}`));

          // Always log ICE state transitions (helps diagnose a leg that connects
          // at the SIP layer but whose media path never comes up on WebKit).
          conn.addEventListener('iceconnectionstatechange', () => {
            dbg(`${opts.failLabel}: iceConnectionState=${conn.iceConnectionState}`);
          });

          if (opts.reportConnectionState && handlers.onConnectionState) {
            const report = (): void => handlers.onConnectionState?.(conn.iceConnectionState);
            conn.addEventListener('iceconnectionstatechange', report);
            report(); // emit the initial state
          }

          if (opts.applyViewerPatch) {
            // Matrix SFU can return wrong/absent direction lines on a viewer answer;
            // patch a=recvonly→sendonly before the native PC sees it.
            const origSetRemoteDescription = conn.setRemoteDescription.bind(conn);
            conn.setRemoteDescription = function patchedSetRemoteDescription(
              desc: RTCSessionDescriptionInit,
            ): Promise<void> {
              if (desc && desc.type === 'answer' && desc.sdp) {
                const fixedSdp = patchAnswerSdp(desc.sdp);
                if (fixedSdp !== desc.sdp) {
                  const patched = new RTCSessionDescription({ type: desc.type, sdp: fixedSdp });
                  // WebKit is far stricter than Chromium about munged SDP and may
                  // REJECT the patched answer (→ the view leg silently never gets a
                  // track on macOS desktop). Fall back to the SFU's ORIGINAL answer
                  // so the leg can still negotiate. Chromium accepts the patch and
                  // never hits the fallback.
                  return origSetRemoteDescription(patched).catch(() => origSetRemoteDescription(desc));
                }
              }
              return origSetRemoteDescription(desc);
            };
          }
        }
      });

      ua.on('disconnected', () => {
        // WS dropped while this attempt was still pending (before OR after the
        // INVITE was placed, but before it confirmed) — transient: retry/fail.
        // After confirm, media rides the PeerConnection, so a signaling-WS drop
        // is NOT a leg loss (and the caller's hangup → stopUa fires this too —
        // `attemptSettled` is already true then, so it's correctly ignored).
        if (!attemptSettled) onLegLost('Connection Error');
      });

      ua.start();
    };

    attemptCall();
  });
}

export function createMediaClient(config: MediaClientConfig): MediaClient {
  return {
    // -----------------------------------------------------------------------
    // join — base AUDIO-ONLY call. Camera + screen are separate SFU sessions
    // (shareCamera / shareScreen); the peer's are received via viewCamera /
    // viewScreen. Reports ICE connection-state for the UI status indicator.
    // -----------------------------------------------------------------------
    join(params: JoinParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { sessionId, meetingId, conferenceId, participantPin, displayName, localStream } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, sessionId, displayName),
          dialTarget: sessionId,
          extraHeaders: [`X-XOP: ${[conferenceId, participantPin, meetingId, sessionId, 'a', 'b'].join('/')}`],
          pcConfig: makePcConfig(config.iceServers),
          mediaStream: localStream,
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
          reportConnectionState: true,
          failLabel: 'call failed',
          disconnectLabel: 'UA disconnected before call could be placed',
        },
        handlers,
      );
    },

    // -----------------------------------------------------------------------
    // consultationDial — OUTBOUND PSTN dial, ported from H&H consultation. Dials
    // the phone NUMBER directly (not a sessionId) with the 2-part moderator
    // X-XOP, so C1 originates the PSTN leg + bridges it to this audio-only
    // WebRTC leg. Full-duplex audio (we send our mic + receive the callee).
    // -----------------------------------------------------------------------
    consultationDial(params: ConsultationParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { sessionId, conferenceId, moderatorPin, number, displayName, localStream } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, sessionId, displayName),
          dialTarget: number,
          extraHeaders: [consultationXopHeader(conferenceId, moderatorPin)],
          pcConfig: makePcConfig(config.iceServers),
          mediaStream: localStream,
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
          reportConnectionState: true,
          failLabel: 'consultation dial failed',
          disconnectLabel: 'UA disconnected before consultation dial could be placed',
        },
        handlers,
      );
    },

    // -----------------------------------------------------------------------
    // shareScreen — publish local screen capture. Dials OWN sessionId, /s/s.
    // -----------------------------------------------------------------------
    shareScreen(params: ScreenShareParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { ownSessionId, meetingId, conferenceId, participantPin, displayName, screenStream } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, ownSessionId, displayName),
          dialTarget: ownSessionId,
          extraHeaders: [screenXopHeader(conferenceId, participantPin, meetingId, ownSessionId)],
          pcConfig: makePcConfig(config.iceServers, true), // relay-only (C1 TURN-only)
          mediaStream: screenStream,
          // SEND-ONLY: the publisher must not offer to receive, so the SFU can't
          // echo the sharer's own screen back down this leg (the "I see my own
          // screen" bug). Mirrors the camera publish leg.
          rtcOfferConstraints: { offerToReceiveAudio: false, offerToReceiveVideo: false },
          failLabel: 'screen share failed',
          disconnectLabel: 'UA disconnected before screen share could start',
        },
        handlers,
      );
    },

    // -----------------------------------------------------------------------
    // viewScreen — subscribe to a remote screen share. Dials the SHARER's
    // sessionId, /s/s. RECEIVE-ONLY: mediaConstraints {false,false} suppresses
    // getUserMedia (no spurious prompt), offerToReceiveVideo pulls their video.
    // -----------------------------------------------------------------------
    viewScreen(params: ViewScreenParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { sharerSessionId, ownSessionId, meetingId, conferenceId, participantPin, displayName } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, ownSessionId, displayName),
          dialTarget: sharerSessionId,
          extraHeaders: [screenXopHeader(conferenceId, participantPin, meetingId, ownSessionId)],
          pcConfig: makePcConfig(config.iceServers, true), // relay-only (C1 TURN-only)
          mediaConstraints: { audio: false, video: false },
          rtcOfferConstraints: { offerToReceiveAudio: false, offerToReceiveVideo: true },
          applyViewerPatch: true,
          addRecvVideoTransceiver: true, // WebKit: offerToReceiveVideo is ignored
          failLabel: 'screen view failed',
          disconnectLabel: 'UA disconnected before screen view could start',
        },
        handlers,
      );
    },

    // -----------------------------------------------------------------------
    // shareCamera — publish the local camera. Dials OWN sessionId, v-<own>/s.
    // -----------------------------------------------------------------------
    shareCamera(params: CameraShareParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { ownSessionId, meetingId, conferenceId, participantPin, displayName, cameraStream } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, ownSessionId, displayName),
          dialTarget: ownSessionId,
          extraHeaders: [cameraPublishXopHeader(conferenceId, participantPin, meetingId, ownSessionId)],
          pcConfig: makePcConfig(config.iceServers, true), // relay-only (C1 TURN-only)
          mediaStream: cameraStream,
          failLabel: 'camera share failed',
          disconnectLabel: 'UA disconnected before camera share could start',
        },
        handlers,
      );
    },

    // -----------------------------------------------------------------------
    // viewCamera — subscribe to a peer's published camera. Dials the SHARER's
    // sessionId, v-<sharer>/r. RECEIVE-ONLY (same suppression as viewScreen) —
    // this is the fix for "the other side gets a camera permission prompt".
    // -----------------------------------------------------------------------
    viewCamera(params: ViewCameraParams, handlers: MediaCallHandlers): Promise<MediaSession> {
      const { sharerSessionId, ownSessionId, meetingId, conferenceId, participantPin, displayName } = params;
      return placeCall(
        {
          buildUa: () => buildUA(config, ownSessionId, displayName),
          dialTarget: sharerSessionId,
          extraHeaders: [cameraViewXopHeader(conferenceId, participantPin, meetingId, ownSessionId, sharerSessionId)],
          pcConfig: makePcConfig(config.iceServers, true), // relay-only (C1 TURN-only)
          mediaConstraints: { audio: false, video: false },
          rtcOfferConstraints: { offerToReceiveAudio: false, offerToReceiveVideo: true },
          applyViewerPatch: true,
          addRecvVideoTransceiver: true, // WebKit: offerToReceiveVideo is ignored
          failLabel: 'camera view failed',
          disconnectLabel: 'UA disconnected before camera view could start',
        },
        handlers,
      );
    },
  };
}
